import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import ParticlesBackground from './ParticlesBackground';
import AppNavbar from './Navbar';
import VolatilityChart from './VolatilityChart';
import VolTrendChart from './VolTrendChart';
import RVIVTimeSeriesChart from './RVIVTimeSeriesChart';
import RVIVSeparateTimeSeriesChart from './RVIVSeparateTimeSeriesChart';
import VolTrendTimeSeriesChart from './VolTrendTimeSeriesChart';
import HeatmapChart from './HeatmapChart';
import ImpliedMovesChart from './ImpliedMovesChart';
import './Models.css';

const API_URL = process.env.REACT_APP_API_URL || "";

const Volatility = () => {
    const { isAuthenticated, loading, error, setError, login } = useAuth();
    const [localEmail, setLocalEmail] = useState("");
    const [dataTimestamp, setDataTimestamp] = useState("");

    useEffect(() => {
        if (!isAuthenticated) return;
        fetch(`${API_URL}/data/volatility`)
            .then(r => r.json())
            .then(({ timestamp }) => setDataTimestamp(timestamp))
            .catch(() => {});
    }, [isAuthenticated]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        await login(localEmail);
    };

    if (loading) {
        return (
            <div className="models-page">
                <header className="models-header">
                    <AppNavbar />
                    <ParticlesBackground />
                    <div className="header-content">
                        <h1>Volatility</h1>
                    </div>
                </header>

                <main className="models-main">
                    <div className="container">
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <p>Loading...</p>
                        </div>
                    </div>
                </main>

                <footer className="models-footer">
                    <div className="container">
                        <p>&copy; 2023-{new Date().getFullYear()} machinaquanta.com. All rights reserved.</p>
                    </div>
                </footer>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="models-page">
                <header className="models-header">
                    <AppNavbar />
                    <ParticlesBackground />
                    <div className="header-content">
                        <h1>Volatility</h1>
                    </div>
                </header>

                <main className="models-main">
                    <div className="container">
                        <div className="chart-container">
                            <div className="charts-vertical blurred">
                                <div className="chart-card">
                                    <h3>S&P 500 RV-IV vs 1M Forward Returns</h3>
                                    <div className="chart-wrapper" style={{ minHeight: '600px' }}>
                                        <VolatilityChart />
                                    </div>
                                </div>
                                <div className="chart-card" style={{ marginTop: '1rem' }}>
                                    <h3>S&P 500 Price vs RV-IV Spread</h3>
                                    <div className="chart-wrapper" style={{ minHeight: '500px' }}>
                                    </div>
                                </div>
                            </div>

                            <div className="login-overlay">
                                <h2>Subscriber Login</h2>
                                <p>Enter your email to access the site.</p>
                                <form onSubmit={handleSubmit}>
                                    <input
                                        type="email"
                                        placeholder="Enter your email"
                                        value={localEmail}
                                        onChange={(e) => setLocalEmail(e.target.value)}
                                        required
                                    />
                                    <button type="submit">Login</button>
                                </form>
                                {error && <p className="error-message">{error}</p>}

                                <iframe
                                    src="https://machinaquanta.substack.com/embed"
                                    title="Substack Subscription Login"
                                    width="480"
                                    height="320"
                                    frameBorder="0"
                                    scrolling="no"
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </main>

                <footer className="models-footer">
                    <div className="container">
                        <p>&copy; 2023-{new Date().getFullYear()} machinaquanta.com. All rights reserved.</p>
                    </div>
                </footer>
            </div>
        );
    }

    return (
        <div className="models-page">
            <header className="models-header">
                <AppNavbar />
                <ParticlesBackground />
                <div className="header-content">
                    <h1>Volatility</h1>
                </div>
            </header>

            <main className="models-main">
                <div className="container">

                    {dataTimestamp && (
                        <p style={{ textAlign: 'right', fontSize: '12px', color: '#999', margin: '0 0 0.5rem' }}>
                            Data last updated: {dataTimestamp}
                        </p>
                    )}

                    {/* Row 1: Heatmap (full width) */}
                    <div className="chart-grid-fullwidth">
                        <div className="chart-card" style={{ padding: '1rem 1rem 0.5rem' }}>
                            <h3 style={{ marginBottom: '0.25rem' }}>S&P 500 Hedging Cost Heatmap</h3>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '14px', color: '#555', fontStyle: 'italic' }}>Where are we on the map? Forward 1-month returns based on current hedging conditions.</p>
                            <HeatmapChart />
                        </div>
                    </div>

                    {/* Row 2: Scatter analysis charts side-by-side */}
                    <div className="chart-grid-2col">
                        <div className="chart-card" style={{ padding: '1rem 1rem 0.5rem' }}>
                            <h3 style={{ marginBottom: '0.25rem' }}>Returns vs RV-IV Spread</h3>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '14px', color: '#555', fontStyle: 'italic' }}>How big is the gap between IV and RV right now?</p>
                            <VolatilityChart />
                        </div>
                        <div className="chart-card" style={{ padding: '1rem 1rem 0.5rem' }}>
                            <h3 style={{ marginBottom: '0.25rem' }}>Returns vs RV-IV Momentum</h3>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '14px', color: '#555', fontStyle: 'italic' }}>Is that gap widening or narrowing compared to recently?</p>
                            <VolTrendChart />
                        </div>
                    </div>

                    {/* Row 3: Implied moves cone + RV vs IV separate */}
                    <div className="chart-grid-2col">
                        <div className="chart-card" style={{ padding: '0.5rem 1rem' }}>
                            <h3 style={{ marginBottom: '0.25rem' }}>SPX Implied Move Projections</h3>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '14px', color: '#555', fontStyle: 'italic' }}>Where could SPX land in 1 day, 1 week, and 1 month based on current IV?</p>
                            <ImpliedMovesChart />
                        </div>
                        <div className="chart-card" style={{ padding: '0.5rem 1rem' }}>
                            <h3 style={{ marginBottom: '0.25rem' }}>S&P 500 Price vs RV &amp; IV</h3>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '14px', color: '#555', fontStyle: 'italic' }}>Realized vs implied volatility over time — when are they diverging?</p>
                            <RVIVSeparateTimeSeriesChart />
                        </div>
                    </div>

                    {/* Row 4: RV-IV spread + Hedging cost trend */}
                    <div className="chart-grid-2col">
                        <div className="chart-card" style={{ padding: '0.5rem 1rem' }}>
                            <h3 style={{ marginBottom: '0.25rem' }}>S&P 500 Price vs RV-IV Spread</h3>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '14px', color: '#555', fontStyle: 'italic' }}>How big is the gap between IV and RV right now?</p>
                            <RVIVTimeSeriesChart />
                        </div>
                        <div className="chart-card" style={{ padding: '0.5rem 1rem' }}>
                            <h3 style={{ marginBottom: '0.25rem' }}>S&P 500 Price vs Hedging Cost Trend</h3>
                            <p style={{ margin: '0 0 0.5rem', fontSize: '14px', color: '#555', fontStyle: 'italic' }}>Are hedging costs accelerating or decelerating?</p>
                            <VolTrendTimeSeriesChart />
                        </div>
                    </div>

                </div>
            </main>

            <footer className="models-footer">
                <div className="container">
                    <p>&copy; 2023-{new Date().getFullYear()} machinaquanta.com. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default Volatility;
